import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SessionDetails from "../components/SessionDetails";
import PromptDisplay from "../components/PromptDisplay";
import EntryForm from "../components/EntryForm";
import CollapsibleSection from "../components/CollapsibleSection";
import { useSettings } from "../context/SettingsContext";
import client from "../api/client";

const CANNABIS_TEMPLATES = [
  { id: "quick_log", label: "⚡ Quick Log", fields: { state: "After" } },
  { id: "full_session", label: "📋 Full Session", fields: {} },
];

const MUSHROOM_TEMPLATES = [
  { id: "before_journey", label: "🌀 Before Journey", fields: { state: "Before" } },
  { id: "during_journey", label: "🌀 During", fields: { state: "During" } },
  { id: "after_journey", label: "🌀 After Journey", fields: { state: "After" } },
  { id: "integration_note", label: "🌀 Integration", fields: { state: "Integration" } },
  { id: "microdose_checkin", label: "🌀 Microdose", fields: { state: "After" } },
];

function MedicineSelect({ onSelect }) {
  const cardBase = {
    flex: 1,
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "16px",
    padding: "32px 24px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.2s, background 0.2s",
    background: "rgba(255,255,255,0.03)",
  };
  const [hovered, setHovered] = useState(null);

  return (
    <main className="NewEntryPage">
      <div style={{ marginTop: "48px", maxWidth: "480px", margin: "48px auto 0" }}>
        <p
  style={{
    textAlign: "center",
    opacity: 0.8,
    fontSize: "16px",
    marginBottom: "40px"
  }}
>
  <strong>What experience are you mapping today?</strong>
  <br />
  <span style={{ fontSize: "14px", opacity: 0.8 }}>
    Cannabis and psychedelic experiences each open different reflection tools.
  </span>
</p>

        <div style={{ display: "flex", gap: "16px" }}>
          {[
            { key: "cannabis", emoji: "🌿", label: "Cannabis", sub: "Strains, sessions,\nand plant journaling", color: "125, 211, 132", hex: "#7dd384" },
            { key: "mushroom", emoji: "🌀", label: "Psychedelics", sub: "Journeys, sessions,\nand journaling", color: "180, 125, 232", hex: "#b47de8" },
          ].map(({ key, emoji, label, sub, color, hex }) => (
            <div
              key={key}
              onClick={() => onSelect(key)}
              onMouseEnter={() => setHovered(key)}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...cardBase,
                borderColor: hovered === key ? `rgba(${color}, 0.5)` : "rgba(255,255,255,0.1)",
                background: hovered === key ? `rgba(${color}, 0.06)` : "rgba(255,255,255,0.03)",
              }}
            >
              <div style={{ fontSize: "44px", marginBottom: "16px" }}>{emoji}</div>
              <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px", color: hovered === key ? hex : "inherit" }}>
                {label}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.5, lineHeight: "1.6", whiteSpace: "pre-line" }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

function MushroomSessionPicker({ onNew, onExisting }) {
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/sessions/recent")
      .then(res => setRecentSessions(res.data.filter(s => s.medicine_type === "mushroom")))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cardBase = {
    flex: 1,
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "16px",
    padding: "28px 24px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.2s, background 0.2s",
    background: "rgba(255,255,255,0.03)",
  };
  const [hovered, setHovered] = useState(null);

  return (
    <main className="NewEntryPage">
      <div style={{ marginTop: "48px", maxWidth: "480px", margin: "48px auto 0" }}>
        <h1 style={{ textAlign: "center", marginBottom: "8px", fontSize: "22px", fontWeight: "500" }}>
          🌀 Psychedelic Entry
        </h1>
        <p style={{ textAlign: "center", opacity: 0.5, fontSize: "14px", marginBottom: "40px" }}>
          Is this a new journey or part of an existing one?
        </p>
        <div style={{ display: "flex", gap: "16px" }}>
          <div
  onMouseEnter={() => setHovered("new")}
  onMouseLeave={() => setHovered(null)}
  onClick={onNew}
  style={{
    ...cardBase,

    // --- Glow system (purple version) ---
    background:
      hovered === "new"
        ? "rgba(180,125,232,0.12)"
        : "rgba(180,125,232,0.06)",

    border:
      hovered === "new"
        ? "1px solid rgba(180,125,232,0.5)"
        : "1px solid rgba(180,125,232,0.35)",

    boxShadow:
      hovered === "new"
        ? "0 0 8px rgba(255,255,220,0.9), 0 0 18px rgba(255,255,240,0.7), 0 0 28px rgba(180,125,232,0.5)"
        : "0 0 4px rgba(255,255,220,0.5), 0 0 10px rgba(255,255,240,0.4), 0 0 16px rgba(180,125,232,0.25)",

    padding: "14px 18px",
    borderRadius: "10px",
    transition: "all 0.22s ease",
    cursor: "pointer",
  }}
>
  <div style={{ fontSize: "36px", marginBottom: "12px" }}>✨</div>

  <div
    style={{
      fontSize: "15px",
      fontWeight: 500,
      marginBottom: "6px",
      color: hovered === "new" ? "#b47de8" : "#dfeaff",
      transition: "color 0.2s ease",
    }}
  >
    New Journey
  </div>

  <div style={{ fontSize: "12px", opacity: 0.5 }}>Start fresh</div>
</div>


          <div
  onMouseEnter={() => setHovered("existing")}
  onMouseLeave={() => setHovered(null)}
  style={{
    ...cardBase,

    // --- Glow + color system matching your button ---
    background: hovered === "existing"
      ? "rgba(111, 216, 255, 0.12)"
      : "rgba(111, 216, 255, 0.06)",

    border: "1px solid rgba(111, 216, 255, 0.35)",
    boxShadow:
      hovered === "existing"
        ? "0 0 8px rgba(255,255,220,0.9), 0 0 18px rgba(255,255,240,0.7), 0 0 28px rgba(111,216,255,0.5)"
        : "0 0 4px rgba(255,255,220,0.5), 0 0 10px rgba(255,255,240,0.4), 0 0 16px rgba(111,216,255,0.25)",

    padding: "14px 18px",
    borderRadius: "10px",
    transition: "all 0.22s ease",

    // --- Disabled state ---
    opacity: loading || recentSessions.length === 0 ? 0.4 : 1,
    cursor: recentSessions.length === 0 ? "default" : "pointer",
  }}
>
  <div style={{ fontSize: "32px", marginBottom: "10px" }}>🔗</div>

  <div
    style={{
      fontSize: "15px",
      fontWeight: 500,
      marginBottom: "4px",
      color: hovered === "existing" ? "#6fd8ff" : "#dfeaff",
      transition: "color 0.2s ease",
    }}
  >
    Add to Journey
  </div>


            <div style={{ fontSize: "12px", opacity: 0.5 }}>
              {loading ? "Loading..." : recentSessions.length === 0 ? "No journeys yet" : "Before, During, After, or Integration"}
            </div>
            {!loading && recentSessions.length > 0 && (
              <div style={{ marginTop: "16px", textAlign: "left" }}>
                {recentSessions.map(s => (
                  <div
                    key={s.id}
                    onClick={(e) => { e.stopPropagation(); onExisting(s); }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      marginBottom: "6px",
                      background: "rgba(180,125,232,0.08)",
                      border: "1px solid rgba(180,125,232,0.2)",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ fontWeight: "500" }}>{s.subcategory}</span>
                    <span style={{ opacity: 0.5, marginLeft: "8px" }}>
                      {new Date(s.session_date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                    </span>
                    <span style={{ opacity: 0.5, marginLeft: "8px", fontSize: "11px" }}>{s.state}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function NewEntryPage({ onSaved }) {
  const { settings } = useSettings();
  const [searchParams] = useSearchParams();
  const [medicineType, setMedicineType] = useState("");
  const [mushroomEnabled, setMushroomEnabled] = useState(true);
  const [appliedTemplate, setAppliedTemplate] = useState(null);
  const [sessionMode, setSessionMode] = useState(null);
  const [linkedSession, setLinkedSession] = useState(null);
  const [recentCannabis, setRecentCannabis] = useState([]);
  const [cannabisBannerDismissed, setCannabisBannerDismissed] = useState(false);
  const [cannabisLinkedSession, setCannabisLinkedSession] = useState(null);
  const [fromSitterSession, setFromSitterSession] = useState(null);
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [prompt, setPrompt] = useState("");
  const [expectedEffects, setExpectedEffects] = useState("");
  const [intentionTags, setIntentionTags] = useState([]);
  const [sessionDetails, setSessionDetails] = useState({
    amount: "", method: "", time_of_day: "", session_timing: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get("/app-settings")
      .then(res => {
        const enabled = res.data.mushroom_enabled === 'true';
        setMushroomEnabled(enabled);
        if (!enabled) setMedicineType("cannabis");
      })
      .catch(() => setMedicineType("cannabis"));
  }, []);

  useEffect(() => {
  const sessionId = searchParams.get("session");
  const medicine = searchParams.get("medicine");
  const subcatParam = searchParams.get("subcategory");
  const methodParam = searchParams.get("method");
  const amountParam = searchParams.get("amount");
  const sessionTimingParam = searchParams.get("session_timing");
  const intentionParam = searchParams.get("intention");

  if (medicine && !sessionId) {
    setMedicineType(medicine);
    setSessionMode("new");
    setSubcategory(subcatParam || "");
    setSessionDetails(prev => ({
      ...prev,
      method: methodParam || "",
      amount: amountParam || "",
      session_timing: sessionTimingParam || "",
    }));
  }

  if (sessionId && medicine) {
    setMedicineType(medicine);
    setSessionMode("existing");
    setLinkedSession({
      id: parseInt(sessionId),
      medicine_type: medicine,
      subcategory: subcatParam || "",
      method: methodParam || "",
      session_date: new Date().toISOString(),
    });
    setSubcategory(subcatParam || "");
    setSessionDetails(prev => ({ ...prev, method: methodParam || "" }));
  }
}, []);

const entryInitialData = useMemo(
  () => ({
    expected_effects: expectedEffects,
    subcategory: subcategory,
    category: category,
  }),
  [expectedEffects, subcategory, category]
);

  useEffect(() => {
    if (medicineType !== "cannabis") return;
    client.get("/sessions/recent")
      .then(res => {
        const recent = res.data.filter(s => s.medicine_type === "cannabis");
        const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
        setRecentCannabis(recent.filter(s => new Date(s.session_date) >= cutoff));
      })
      .catch(() => {});
  }, [medicineType]);

  const handleSelectExistingSession = (session) => {
    setLinkedSession(session);
    setSessionMode("existing");
    setSubcategory(session.subcategory || "");
    setSessionDetails(prev => ({
      ...prev,
      method: session.method || "",
      amount: session.amount || "",
    }));
  };

  const resetForm = () => {
    setCategory(""); setState(""); setSubcategory(""); setPrompt("");
    setExpectedEffects(""); setIntentionTags([]);
    setSessionDetails({ amount: "", method: "", time_of_day: "", session_timing: "" });
    setLinkedSession(null); setSessionMode(null);
    setCannabisLinkedSession(null); setCannabisBannerDismissed(false);
    setAppliedTemplate(null);
  };

  const handleSubmit = async (entryData) => {
    setIsSubmitting(true);
    setError("");
    try {
      const payload = {
        title: entryData.title,
        content: entryData.content,
        session_date: entryData.session_date,
        category,
        state,
        subcategory,
        medicine_type: medicineType,
        prompt_text: prompt,
        amount: sessionDetails.amount,
        method: sessionDetails.method,
        time_of_day: sessionDetails.time_of_day,
        session_timing: sessionDetails.session_timing,
        strain_name: subcategory,
        mood_before: entryData.mood_before,
        mood_after: entryData.mood_after,
        intention: entryData.intention,
        intention_tags: intentionTags,
        intention_met: entryData.intention_met,
        expected_effects: entryData.expected_effects,
        actual_effects: entryData.actual_effects,
        chakra_checkins: entryData.chakra_checkins,
        archetypal_themes: entryData.archetypal_themes,
        body_sensations: entryData.body_sensations,
        nervous_system_before: entryData.nervous_system_before,
        nervous_system_after: entryData.nervous_system_after,
        session_id: linkedSession?.id || cannabisLinkedSession?.id || null,
        sitter_session_id: fromSitterSession?.id || null,
      };
      console.log("Submitting entry payload:", payload);
      await client.post("/entries", payload);
      onSaved();
    } catch (err) {
      setError("Something went wrong saving your entry. " + (err.response?.data?.message || ""));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mushroomEnabled && !medicineType) return <MedicineSelect onSelect={setMedicineType} />;
  if (!medicineType) return null;
  if (medicineType === "mushroom") {
    return (
      <MushroomSessionPicker
        onNew={() => setSessionMode("new")}
        onExisting={handleSelectExistingSession}
      />
    );
  }

  const sectionEmoji = medicineType === "mushroom" ? "🌀" : "🌿";
  

  return (
    <main className="NewEntryPage">
      <div style={{ marginTop: "16px", marginBottom: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
        {(
          <span
            onClick={() => {
              if (medicineType === "mushroom") { setSessionMode(null); }
              else { setMedicineType(""); resetForm(); }
            }}
            style={{ fontSize: "13px", opacity: 0.6, cursor: "pointer" }}
          >
            {medicineType === "mushroom" && sessionMode ? "← Change journey" : "← Change"}
          </span>
        )}
        <span style={{ fontSize: "12px", opacity: 0.6, letterSpacing: "1px", textTransform: "uppercase" }}>
          {medicineType === "mushroom" ? "🌀 Psychedelics" : "🌿 Cannabis"}
          {linkedSession ? ` · ${linkedSession.subcategory}` : ""}
        </span>
      </div>

      {medicineType === "cannabis" && !cannabisBannerDismissed && recentCannabis.length > 0 && (
        <div style={{
          background: "rgba(125,211,132,0.07)",
          border: "1px solid rgba(125,211,132,0.25)",
          borderRadius: "10px",
          padding: "12px 16px",
          marginBottom: "16px",
          fontSize: "13px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}>
          <span style={{ opacity: 0.8 }}>
            Continue from <strong>{recentCannabis[0].subcategory.replace(/-/g, " ")}</strong>·{" "}
            {new Date(recentCannabis[0].session_date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}?
          </span>
          <span
            onClick={() => {
              setCannabisLinkedSession(recentCannabis[0]);
              setCannabisBannerDismissed(true);
              setSubcategory(recentCannabis[0].subcategory);
              setSessionDetails(prev => ({ ...prev, method: recentCannabis[0].method || "" }));
            }}
            style={{ color: "#7dd384", cursor: "pointer", fontWeight: "500" }}
          >
            Yes, link it
          </span> | 
          <span
            onClick={() => setCannabisBannerDismissed(true)}
            style={{ opacity: 0.85, cursor: "pointer" }}
          >
            No, new session
          </span>
          {recentCannabis.length > 1 && (
            <select
              onChange={e => {
                const s = recentCannabis.find(r => r.id === parseInt(e.target.value));
                if (s) {
                  setCannabisLinkedSession(s);
                  setCannabisBannerDismissed(true);
                  setSubcategory(s.subcategory);
                  setSessionDetails(prev => ({ ...prev, method: s.method || "" }));
                }
              }}
              style={{ fontSize: "12px", background: "transparent", border: "1px solid rgba(125,211,132,0.3)", borderRadius: "6px", padding: "2px 6px", color: "inherit", opacity: 0.7 }}
            >
              <option value="">Other recent...</option>
              {recentCannabis.slice(1).map(s => (
                <option key={s.id} value={s.id}>
                  {s.subcategory} · {new Date(s.session_date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div style={{ marginTop: "8px" }}>
        <CollapsibleSection title={`${sectionEmoji} Session Details`} defaultOpen={true}>
          <SessionDetails
            category={category} onCategoryChange={setCategory}
            state={state} onStateChange={setState}
            subcategory={subcategory} onSubcategoryChange={setSubcategory}
            value={sessionDetails}
            onChange={(updated) => setSessionDetails(prev => ({ ...prev, ...updated }))}
            medicineType={medicineType}
          />
        </CollapsibleSection>
      </div>

      <EntryForm
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        state={state}
        category={category}
        subcategory={subcategory}
        intentionTags={intentionTags}
        onIntentionTagsChange={setIntentionTags}
        initialData={entryInitialData}
        medicineType={medicineType}
      />
    </main>
  );
}

export default NewEntryPage;